// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { useEffect, useMemo, useState } from 'react';

/** One rendered flyover: the 16:9 video, and the 3:5 phone render where it has been made. */
export type FlyoverVideo = {
  url: string;
  portrait: { url: string; width: number; height: number } | null;
};

/**
 * Pre-rendered flyover videos per hole, when the offline render script has produced them
 * for this course. An empty map is the ordinary answer — most courses have none — and any
 * failure reads the same way: the 2D flyover simply plays instead.
 */
export const useFlyoverVideos = (
  apiBaseUrl: string,
  token: string | null | undefined,
  courseId: string | null,
  enabled: boolean
): Record<number, FlyoverVideo> => {
  const [byHole, setByHole] = useState<Record<number, FlyoverVideo>>({});

  useEffect(() => {
    if (!enabled || !token || !courseId) {
      setByHole({});
      return undefined;
    }

    let cancelled = false;
    const load = async () => {
      try {
        const response = await fetch(
          `${apiBaseUrl}/api/geodata/courses/${encodeURIComponent(courseId)}/flyovers`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (!response.ok || cancelled) return;
        const payload = (await response.json()) as {
          holes?: {
            holeNumber?: number;
            url?: string;
            portrait?: { url?: string; width?: number; height?: number } | null;
          }[];
        };
        if (cancelled) return;
        const next: Record<number, FlyoverVideo> = {};
        for (const hole of payload.holes ?? []) {
          if (!hole.holeNumber || !hole.url) continue;
          const portrait = hole.portrait;
          next[hole.holeNumber] = {
            url: hole.url,
            portrait:
              portrait?.url && portrait.width && portrait.height
                ? { url: portrait.url, width: portrait.width, height: portrait.height }
                : null,
          };
        }
        setByHole(next);
      } catch {
        // Decoration only; the 2D flyover needs nothing from here.
      }
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, [apiBaseUrl, courseId, enabled, token]);

  return byHole;
};

/** The landscape URL per hole — what the tour preview's 16:9 player wants. */
export const useLandscapeFlyoverUrls = (
  apiBaseUrl: string,
  token: string | null | undefined,
  courseId: string | null,
  enabled: boolean
): Record<number, string> => {
  const byHole = useFlyoverVideos(apiBaseUrl, token, courseId, enabled);
  return useMemo(
    () => Object.fromEntries(Object.entries(byHole).map(([hole, video]) => [hole, video.url])),
    [byHole]
  );
};
